import type { MutableRefObject } from "react"
import { v4 as uuidv4 } from "uuid"

import { objectEntries } from "../../../utils/helpers"
import type { ChatPart, ModelId } from "../types"

export interface SSEData {
  type: string
  content?: string
  session_id?: string
  index?: number
  timestamp?: string
  status?: string
  part_id?: string
  message_id?: string
  entity_id?: string
  human_readable_name?: string
  model?: ModelId
  chunk_mode?: "append" | "replace"
  seq?: number
  event_id?: string
  final?: boolean
}

export interface AppendChunkState {
  chunksBySeq: Record<number, string>
}

export const ASSISTANT_STREAM_TYPES = [
  "text",
  "thought",
  "tool_call",
  "tool_response",
  "code_execution",
  "code_result",
  "agent_transfer",
] as const

export const isAssistantStreamType = (
  type: string
): type is (typeof ASSISTANT_STREAM_TYPES)[number] =>
  (ASSISTANT_STREAM_TYPES as readonly string[]).includes(type)

export const resolvePartId = ({
  partId,
  messageId,
  index,
}: {
  partId?: string
  messageId?: string
  index?: number
}): string =>
  partId ?? (messageId != null ? `${messageId}_${index ?? 0}` : uuidv4())

export const removePartsForSession = (
  parts: Record<string, ChatPart>,
  sessionToRemove: string
): Record<string, ChatPart> =>
  Object.fromEntries(
    objectEntries(parts).filter(
      ([, part]) => part.session_id !== sessionToRemove
    )
  )

export const finalizeLatestAssistantTurn = (
  parts: Record<string, ChatPart>,
  targetSession: string
): Record<string, ChatPart> => {
  const sessionAssistantParts = Object.values(parts)
    .filter(
      part =>
        part.session_id === targetSession &&
        part.role === "assistant" &&
        part.message_id != null
    )
    .sort((a, b) => {
      const tA = new Date(a.timestamp).getTime()
      const tB = new Date(b.timestamp).getTime()
      if (tA === tB) {
        return (a.index ?? 0) - (b.index ?? 0)
      }
      return tA - tB
    })

  const latestMessageId = sessionAssistantParts.at(-1)?.message_id
  if (latestMessageId == null) {
    return parts
  }

  let changed = false
  const next = { ...parts }

  for (const [key, value] of objectEntries(parts)) {
    if (
      value.session_id === targetSession &&
      value.role === "assistant" &&
      value.message_id === latestMessageId &&
      value.chunk_mode === "append" &&
      value.final !== true
    ) {
      next[key] = { ...value, final: true }
      changed = true
    }
  }

  return changed ? next : parts
}

export const hasResponseStatus = (error: unknown, status: number): boolean => {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return false
  }
  const { response } = error as { response?: { status?: number } }
  return response?.status === status
}

export const resolveChunkMode = (data: SSEData): "append" | "replace" => {
  if (data.chunk_mode === "append" || data.chunk_mode === "replace") {
    return data.chunk_mode
  }
  return data.type === "text" || data.type === "thought" ? "append" : "replace"
}

export const mergeByChunkMode = (
  previous: string,
  incoming: string,
  mode: "append" | "replace"
): string => (mode === "append" ? `${previous}${incoming}` : incoming)

export const getStreamPartFields = (
  streamData: SSEData
): Pick<ChatPart, "chunk_mode" | "seq" | "event_id" | "final"> => ({
  chunk_mode: resolveChunkMode(streamData),
  seq: streamData.seq,
  event_id: streamData.event_id,
  final: streamData.final,
})

export const applyStreamPartUpdate = (
  appendChunkStateRef: MutableRefObject<Record<string, AppendChunkState>>,
  prev: Record<string, ChatPart>,
  partId: string,
  incomingPart: ChatPart,
  streamData: SSEData
): Record<string, ChatPart> => {
  const seq = streamData.seq
  const mode = resolveChunkMode(streamData)
  const existing = prev[partId]
  const existingSeq = existing?.seq
  const previousContent = existing?.content ?? ""

  // SSE transports can replay the same event after reconnect; keep updates idempotent.
  if (
    existing?.event_id != null &&
    streamData.event_id != null &&
    existing.event_id === streamData.event_id
  ) {
    return prev
  }

  if (
    mode === "replace" &&
    seq != null &&
    existingSeq != null &&
    seq <= existingSeq
  ) {
    return prev
  }

  const incomingContent = incomingPart.content
  let mergedContent = incomingContent
  const highestSeq =
    seq != null && existingSeq != null
      ? Math.max(existingSeq, seq)
      : (seq ?? existingSeq)
  const finalValue = existing?.final === true || streamData.final === true
  const shouldUseIncomingEventId =
    streamData.event_id != null &&
    (seq == null || existingSeq == null || seq >= existingSeq)
  const mergedEventId = shouldUseIncomingEventId
    ? streamData.event_id
    : existing?.event_id

  if (mode === "append") {
    if (seq != null) {
      const state =
        appendChunkStateRef.current[partId] ??
        ({ chunksBySeq: {} } as AppendChunkState)
      appendChunkStateRef.current[partId] = state

      // Some backends emit a full snapshot as a later append chunk (with seq).
      // In that case, replace chunk history with the snapshot to avoid doubling.
      if (
        previousContent.length > 0 &&
        incomingContent.startsWith(previousContent)
      ) {
        state.chunksBySeq = { [seq]: incomingContent }
        mergedContent = incomingContent
      } else {
        state.chunksBySeq[seq] = incomingContent

        mergedContent = Object.keys(state.chunksBySeq)
          .map(Number)
          .sort((a, b) => a - b)
          .map(key => state.chunksBySeq[key] ?? "")
          .join("")
      }
    } else {
      if (previousContent.length === 0) {
        mergedContent = incomingContent
      } else if (
        incomingContent === previousContent ||
        previousContent.endsWith(incomingContent)
      ) {
        // Likely replay of a delta chunk.
        mergedContent = previousContent
      } else if (incomingContent.startsWith(previousContent)) {
        // Some backends emit cumulative snapshots without seq metadata.
        mergedContent = incomingContent
      } else {
        // True delta chunk fallback.
        mergedContent = `${previousContent}${incomingContent}`
      }
    }
  }

  const mergedPart: ChatPart =
    existing == null
      ? {
          ...incomingPart,
          ...getStreamPartFields(streamData),
          content: mergedContent,
          final: finalValue,
          seq: highestSeq,
          event_id: mergedEventId,
        }
      : {
          ...existing,
          ...incomingPart,
          ...getStreamPartFields(streamData),
          content: mergedContent,
          final: finalValue,
          seq: highestSeq,
          event_id: mergedEventId,
        }

  return { ...prev, [partId]: mergedPart }
}

export class StopRetryError extends Error {}

/** Thrown from onclose/onerror when the stream dropped unexpectedly and a
 *  reconnect attempt should be made. */
export class ReconnectNeededError extends Error {}
